import React, { useContext, useEffect, useState } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Collapse from '@mui/material/Collapse';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import InfoRoundedIcon from '@mui/icons-material/InfoRounded';
import useMediaQuery from '@mui/material/useMediaQuery';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import ExpandLessRoundedIcon from '@mui/icons-material/ExpandLessRounded';
import ExpandMoreRoundedIcon from '@mui/icons-material/ExpandMoreRounded';

import { MDropdown } from 'common';
import { AuthContext } from 'authentication/AuthContext';

import { useGetAffiliateOptions, useUserRolePermissions } from 'hooks';
import { useNavigate, useParams } from 'react-router-dom';
import * as R from 'ramda';
import { add, isAfter, isBefore, isValid, parse, startOfDay, sub } from 'date-fns';

import { arrayOf, func, shape } from 'prop-types';

import { userRoles } from 'utils/currentUserRoles';
import { toCentralDateParam } from 'hooks/usePersistentFilters';
import { useGetAllocationGroupsQuery, useGetProductGroupsQuery } from '../dashboardEndpoints';
// eslint-disable-next-line sort-keys-fix/sort-keys-fix
const filterStyle = { maxWidth: { xs: 'initial', lg: 200 }, width: 1 };
const endDateBeforeStartDateMessage = 'End date must be after start date';
const endDateOutsideRangeMessage = 'Cannot exceed 90 days';
const endDateTodayOrFutureMessage = 'Must be less than today';

const renderInput = R.curry((label, params) => (
  <TextField
    {...params}
    label={label}
    slotProps={{
      htmlInput: { ...params.inputProps },

      input: {
        ...params.InputProps,
        endAdornment: params.InputProps.endAdornment,
      },
    }}
  />
));

const FilterPanel = ({ allReports, filters, reportType, setFilters, setReportType }) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const navigate = useNavigate();
  const { view } = useParams();

  const { affiliates, setCurrentSelectedAffiliate } = useContext(AuthContext);
  const { currentAffiliate, isInventoryOwner, isSupplier } = useUserRolePermissions();
  const [collapseOpen, setCollapseOpen] = useState(true);

  const affiliateOptionsType = R.propEq('value', 'BillOfLadingSupplier', reportType)
    ? [userRoles.SUPPLIER]
    : [userRoles.INVENTORY_OWNER];

  const affiliatesOptions = useGetAffiliateOptions(affiliateOptionsType);
  const currentFilters = R.prop(reportType.value, filters);
  const endDate = R.prop('endDate', currentFilters);
  const startDate = R.prop('startDate', currentFilters);
  const allocationGroup = R.prop('allocationGroup', currentFilters);
  const [draftEndDate, setDraftEndDate] = useState(R.defaultTo(null, endDate));

  const reports = R.compose(
    R.sortBy(R.prop('type')),
    R.reject(report => R.not(isSupplier) && R.equals(report.value, 'BillOfLadingSupplier')),
    R.reject(report => R.not(isInventoryOwner) && R.equals(report.value, 'BillOfLadingInvOwner'))
  )(allReports);

  const isHistorical = R.any(R.equals(true), [
    R.equals('BookingHistory', R.prop('value', reportType)),
    R.equals('HistoricalInventory', R.prop('value', reportType)),
    R.equals('BillOfLadingInvOwner', R.prop('value', reportType)),
    R.equals('BillOfLadingSupplier', R.prop('value', reportType)),
  ]);

  const isBol = R.any(R.equals(true), [
    R.equals('BillOfLadingInvOwner', R.prop('value', reportType)),
    R.equals('BillOfLadingSupplier', R.prop('value', reportType)),
  ]);
  const isHistoricalInventory = R.equals('HistoricalInventory', R.prop('value', reportType));
  const hasNinetyDayDateRange = R.any(R.equals(true), [
    R.equals('BookingHistory', R.prop('value', reportType)),
    isHistoricalInventory,
  ]);
  const today = startOfDay(new Date());
  const maxHistoricalInventoryEndDate = sub(today, { days: 1 });
  const ninetyDayMaxEndDate = R.and(hasNinetyDayDateRange, isValid(startDate))
    ? add(startDate, { days: 90 })
    : null;
  const maxEndDate = R.and(isHistoricalInventory, isValid(ninetyDayMaxEndDate))
    ? R.min(ninetyDayMaxEndDate, maxHistoricalInventoryEndDate)
    : ninetyDayMaxEndDate;

  const getEndDateValidationMessage = value => {
    if (R.and(R.and(isValid(startDate), isValid(value)), isBefore(value, startDate))) {
      return endDateBeforeStartDateMessage;
    }

    if (
      R.and(isHistoricalInventory, R.and(isValid(value), R.not(isBefore(startOfDay(value), today))))
    ) {
      return endDateTodayOrFutureMessage;
    }

    if (R.and(R.and(isValid(maxEndDate), isValid(value)), isAfter(value, maxEndDate))) {
      return endDateOutsideRangeMessage;
    }

    return '';
  };
  const endDateValidationMessage = getEndDateValidationMessage(draftEndDate);
  const hasEndDateValidationError = R.not(R.isEmpty(endDateValidationMessage));
  const datesSelected = R.and(
    R.and(R.not(R.isEmpty(startDate)), R.not(R.isEmpty(draftEndDate))),
    R.and(R.and(isValid(startDate), isValid(draftEndDate)), R.not(hasEndDateValidationError))
  );

  const optionalParams = {
    endDate: R.and(isHistorical, datesSelected) ? toCentralDateParam(draftEndDate, 'end') : null,
    startDate: R.and(isHistorical, datesSelected) ? toCentralDateParam(startDate, 'start') : null,
  };

  const filtered = R.reject(R.equals(null))(optionalParams);

  const params = {
    ...filtered,
    inventoryOwner: {
      companyCustomerIdName: currentAffiliate?.customerIdName,
      dataCompanyId: currentAffiliate?.dataCompanyId,
    },
    reportType: R.prop('value', reportType),
  };

  const skipArr = [R.isNil(currentAffiliate), R.isNil(reportType)];

  const skipHistorical = [
    R.and(isHistorical, R.not(datesSelected)),
    R.isNil(draftEndDate),
    R.isNil(startDate),
  ];

  const { data: allocationGroups = [], isFetching: isFetchingAllocations } =
    useGetAllocationGroupsQuery(
      { params },
      {
        refetchOnMountOrArgChange: true,
        skip: R.any(R.equals(true))([
          ...(isHistorical ? skipHistorical : skipArr),
          R.isNil(currentAffiliate),
        ]),
      }
    );

  const productParams = {
    ...params,
    allocationGroupId: R.prop('groupId', allocationGroup),
  };

  const { data: productGroups = [], isFetching: isFetchingProducts } = useGetProductGroupsQuery(
    { params: productParams },
    {
      refetchOnMountOrArgChange: true,
      skip: R.or(R.isNil(R.prop('groupId', allocationGroup)), R.isNil(currentAffiliate)),
    }
  );

  const handleReportChange = value => {
    setReportType(value);
  };

  const handleAffiliateChange = value => {
    const split = value.split('-');
    const affiliateIndex = affiliates.findIndex(
      ({ customerIdName, dataCompanyId: id }) =>
        split[0] === customerIdName && parseInt(split[1], 10) === id
    );
    const affiliate = affiliates[affiliateIndex];
    setFilters(
      R.evolve({
        filters,
        [reportType.value]: R.evolve({
          allocationGroup: R.always(null),
          endDate: R.always(null),
          productGroup: R.always(null),
          startDate: R.always(null),
        }),
      })
    );
    setCurrentSelectedAffiliate(affiliateIndex);
    navigate(
      `/affiliate/${affiliate?.customerIdName}/${affiliate?.dataCompanyId}/inventory-management/inventory-dashboard/${view}`,
      {
        replace: true,
      }
    );
  };

  const handleStartDateChange = value => {
    const time = value;
    let endDateTime = currentFilters?.endDate ?? new Date();
    if (reportType.value === 'BookingHistory') {
      const now = new Date();
      const currentDate = new Date(now.setHours(0, 0, 0, 0));
      const ninetyDaysFromStartDate = add(time, { days: 90 });
      endDateTime = isBefore(ninetyDaysFromStartDate, currentDate)
        ? ninetyDaysFromStartDate
        : currentDate;
    }
    if (
      reportType.value === 'BillOfLadingInvOwner' ||
      reportType.value === 'BillOfLadingSupplier'
    ) {
      const endOfToday = new Date();
      endOfToday.setHours(23, 59, 59, 0);
      endDateTime = endOfToday;
    }
    if (isValid(time)) {
      setFilters(
        R.evolve({
          filters,
          [reportType.value]: R.evolve(currentFilters, {
            allocationGroup: R.always(null),
            endDate: R.always(reportType.value === 'HistoricalInventory' ? null : endDateTime),
            productGroup: R.always(null),
            startDate: R.always(time),
          }),
        })
      );
    }
  };

  const persistEndDate = time => {
    setDraftEndDate(time);
    setFilters({
      ...filters,
      [reportType.value]: R.evolve(
        {
          allocationGroup: R.always(null),
          endDate: R.always(time),
          productGroup: R.always(null),
        },
        currentFilters
      ),
    });
  };

  const handleEndDateChange = (value, context) => {
    const time = value;
    if (
      R.and(
        isValid(time),
        R.and(R.isNil(context?.validationError), R.isEmpty(getEndDateValidationMessage(time)))
      )
    ) {
      persistEndDate(time);
    }
  };

  const handleEndDateBlur = event => {
    const time = parse(event.target.value, 'MM/dd/yyyy', new Date());

    if (isValid(time)) {
      persistEndDate(time);
    }
  };

  const handleEndDateAccept = value => {
    const time = value;
    const validationMessage = getEndDateValidationMessage(time);

    if (R.and(isValid(time), R.isEmpty(validationMessage))) {
      persistEndDate(time);
    }
  };

  const handlePipelineChange = val => {
    setFilters({
      ...filters,
      [reportType.value]: {
        ...currentFilters,
        allocationGroup: val,
        productGroup: null,
      },
    });
  };

  const handleProductChange = val => {
    setFilters({
      ...filters,
      [reportType.value]: {
        ...currentFilters,
        productGroup: val,
      },
    });
  };

  const disableCheck = [R.isNil(reportType)];
  const historicalCheck = [
    R.isNil(startDate),
    R.isNil(draftEndDate),
    R.and(isHistorical, R.not(datesSelected)),
  ];

  const isDisabledAllocationGroup = R.any(R.equals(true))(
    isHistorical ? historicalCheck : disableCheck
  );

  useEffect(() => {
    setDraftEndDate(R.defaultTo(null, endDate));
  }, [endDate, reportType.value]);

  useEffect(() => {
    // if the currently selected affiliate is not available after affiliates change,
    // update currently selected affiliate to the first affiliate option available
    if (R.not(R.isNil(currentAffiliate))) {
      const currentAffiliateAvailable = R.find(
        R.propEq('customerIdName', currentAffiliate.customerIdName),
        affiliatesOptions
      );

      if (R.not(currentAffiliateAvailable)) {
        const [selectedAffiliate] = affiliatesOptions;

        if (selectedAffiliate) {
          handleAffiliateChange(selectedAffiliate.value);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [affiliatesOptions]);

  const FilterGroup = () => (
    <>
      <Autocomplete
        disableClearable
        getOptionLabel={R.prop('type')}
        isOptionEqualToValue={R.eqProps('value')}
        onChange={R.compose(handleReportChange, R.nthArg(1))}
        options={reports}
        renderInput={renderInput('Report Type')}
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        sx={{ maxWidth: { xs: 'initial', lg: 250 }, width: 1 }}
        value={reportType}
      />
      {affiliatesOptions.length > 1 && (
        <Box sx={filterStyle}>
          <MDropdown
            id="affiliate-select"
            items={affiliatesOptions}
            label="Affiliate"
            name="affiliate"
            onChange={({ value }) => handleAffiliateChange(value)}
            value={`${currentAffiliate?.customerIdName}-${currentAffiliate?.dataCompanyId}`}
          />
        </Box>
      )}
      <DatePicker
        disabled={R.or(R.not(isHistorical), R.isNil(reportType))}
        disableFuture
        label={
          R.equals('BookingHistory', R.prop('value', reportType))
            ? 'Booked Start'
            : R.equals('HistoricalInventory', R.prop('value', reportType))
            ? 'Historical Start'
            : 'Load Start Date'
        }
        maxDate={sub(new Date(), {
          days: R.equals('BookingHistory', R.prop('value', reportType)) ? 0 : isBol ? 0 : 1,
        })}
        onAccept={handleStartDateChange}
        value={currentFilters.startDate}
      />
      <DatePicker
        disabled={R.or(R.not(isHistorical), R.isNil(currentFilters.startDate))}
        disableFuture
        label={
          R.equals('BookingHistory', R.prop('value', reportType))
            ? 'Booked End'
            : R.equals('HistoricalInventory', R.prop('value', reportType))
            ? 'Historical End'
            : 'Load End Date'
        }
        maxDate={maxEndDate}
        onAccept={handleEndDateAccept}
        onChange={handleEndDateChange}
        slotProps={{
          textField: {
            error: hasEndDateValidationError,
            helperText: endDateValidationMessage,
            onBlur: handleEndDateBlur,
          },
        }}
        value={draftEndDate}
      />
      <Autocomplete
        disabled={isDisabledAllocationGroup}
        getOptionLabel={o => R.defaultTo('', R.prop('groupName', o))}
        isOptionEqualToValue={R.eqProps('groupId')}
        loading={isFetchingAllocations}
        noOptionsText="No Options"
        onChange={R.flip(handlePipelineChange)}
        options={allocationGroups}
        renderInput={inputParams => (
          <TextField
            {...inputParams}
            label="Pipeline System"
            slotProps={{
              ...inputParams.slotProps,
              htmlInput: {
                ...inputParams.inputProps,
                ...(inputParams.slotProps?.htmlInput || {}),
              },
              input: {
                ...inputParams.InputProps,
                ...(inputParams.slotProps?.input || {}),
                endAdornment: (
                  <>
                    {R.isEmpty(allocationGroups) && (
                      <Tooltip title="No options available">
                        <InfoRoundedIcon fontSize="small" sx={{ color: 'graphic.main', mr: 0.5 }} />
                      </Tooltip>
                    )}
                    {inputParams.InputProps?.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        // eslint-disable-next-line sort-keys-fix/sort-keys-fix
        sx={{ maxWidth: { xs: 'initial', lg: 385 }, width: 1 }}
        value={currentFilters.allocationGroup}
      />
      <Autocomplete
        disabled={isFetchingAllocations || R.isNil(R.prop('groupId', allocationGroup))}
        getOptionLabel={o => R.defaultTo('', R.prop('groupName', o))}
        isOptionEqualToValue={R.eqProps('groupId')}
        loading={isFetchingAllocations || isFetchingProducts}
        noOptionsText="No Options"
        onChange={R.flip(handleProductChange)}
        options={
          R.isEmpty(productGroups)
            ? []
            : [
                {
                  groupId: productGroups.map(p => p.groupId),
                  groupName: 'ALL PRODUCTS',
                },
                ...productGroups
                  .filter(p => p.groupName !== 'ALL PRODUCTS')
                  .map(p => ({ groupId: [p.groupId], groupName: p.groupName })),
              ]
        }
        renderInput={inputParams => (
          <TextField
            {...inputParams}
            label="Product Group"
            slotProps={{
              ...inputParams.slotProps,
              htmlInput: {
                ...inputParams.inputProps,
                ...(inputParams.slotProps?.htmlInput || {}),
              },
              input: {
                ...inputParams.InputProps,
                ...(inputParams.slotProps?.input || {}),
                endAdornment: (
                  <>
                    {!isFetchingProducts &&
                      !R.isNil(R.prop('groupId', allocationGroup)) &&
                      R.isEmpty(productGroups) && (
                        <Tooltip title="No options available">
                          <InfoRoundedIcon
                            fontSize="small"
                            sx={{ color: 'graphic.main', mr: 0.5 }}
                          />
                        </Tooltip>
                      )}
                    {inputParams.InputProps?.endAdornment}
                  </>
                ),
              },
            }}
          />
        )}
        sx={filterStyle}
        value={currentFilters.productGroup}
      />
    </>
  );

  return (
    <>
      {lgUp ? (
        <Stack direction="row" spacing={2}>
          {FilterGroup()}
        </Stack>
      ) : (
        <>
          <Collapse in={collapseOpen} timeout="auto" unmountOnExit>
            <Stack direction="column" spacing={2}>
              {FilterGroup()}
            </Stack>
          </Collapse>
          <Button
            onClick={() => setCollapseOpen(!collapseOpen)}
            sx={{
              backgroundColor: 'transparent',
              mt: collapseOpen ? 2 : 0,
              px: 0,
              py: 1,
            }}
            variant="text"
          >
            {collapseOpen ? <ExpandLessRoundedIcon /> : <ExpandMoreRoundedIcon />}
            {collapseOpen ? 'Collapse Filters' : 'Expand Filters'}
          </Button>
        </>
      )}
    </>
  );
};

FilterPanel.propTypes = {
  allReports: arrayOf(shape({})).isRequired,
  filters: shape({}).isRequired,
  reportType: shape({}),
  setFilters: func.isRequired,
  setReportType: func.isRequired,
};

FilterPanel.defaultProps = {
  reportType: {
    type: 'Inventory Projections',
    value: 'InventoryProjections',
  },
};

export default FilterPanel;
